/**
 * Accordion List Sidebar Component
 *
 * Light DOM Web Component that provides sticky navigation for accordion-list.
 * Highlights current category on scroll and enables smooth scrolling.
 * Uses IntersectionObserver for performant scroll tracking.
 *
 * Usage: <accordion-list-sidebar>...server-rendered nav...</accordion-list-sidebar>
 *
 * @package Felix
 * @since 1.0.0
 */

import { isMobile, isDesktop } from '../shared/utils.js'

class AccordionListSidebar extends HTMLElement {
	getHeaderOffset() {
		const rootStyles = getComputedStyle(document.documentElement)
		const headerHeight = parseFloat(rootStyles.getPropertyValue('--felix-header-height')) || 56
		const announcementHeight = parseFloat(rootStyles.getPropertyValue('--felix-announcement-bar-visible-height')) || 0
		return headerHeight + announcementHeight
	}

	connectedCallback() {
		this.links = this.querySelectorAll('.accordion-list-sidebar__link')
		this.toggle = this.querySelector('.accordion-list-sidebar__toggle')
		this.nav = this.querySelector('.accordion-list-sidebar__nav')
		this.sections = []

		// Header offset for scroll (adjust based on sticky header height)
		this.scrollOffset = 120

		// Get all target sections from links
		this.links.forEach((link) => {
			const href = link.getAttribute('href')
			if (!href || !href.startsWith('#')) return

			const id = href.slice(1)
			const section = document.getElementById(id)

			if (section) {
				this.sections.push({ id, section, link })
			}
		})

		if (this.sections.length === 0) return

		// Mobile toggle
		if (this.toggle && this.nav) {
			this.toggle.addEventListener('click', () => {
				this.nav.classList.toggle('accordion-list-sidebar__nav--active')
			})
		}

		// Set first link as active by default
		this.setActive(this.sections[0].id)

		// Add smooth scroll with offset
		this.initSmoothScroll()

		// Initialize scroll tracking
		this.initObserver()

		// Initialize mobile sticky behavior
		this.initMobileSticky()
	}

	initSmoothScroll() {
		this.links.forEach((link) => {
			link.addEventListener('click', (e) => {
				e.preventDefault()

				const href = link.getAttribute('href')
				if (!href || !href.startsWith('#')) return

				const id = href.slice(1)
				const section = document.getElementById(id)

				if (section) {
					const targetPosition = section.getBoundingClientRect().top + window.scrollY - this.scrollOffset

					window.scrollTo({
						top: targetPosition,
						behavior: 'smooth',
					})

					// Update URL hash without jumping
					history.pushState(null, '', href)

					// Close mobile nav after click
					if (this.nav) {
						this.nav.classList.remove('accordion-list-sidebar__nav--active')
					}
				}
			})
		})
	}

	initObserver() {
		// rootMargin: account for header offset
		// Top margin pushes trigger point down, bottom margin limits active zone
		const options = {
			rootMargin: `-${this.scrollOffset}px 0px -70% 0px`,
			threshold: 0,
		}

		this.observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					this.setActive(entry.target.id)
				}
			})
		}, options)

		this.sections.forEach(({ section }) => {
			this.observer.observe(section)
		})
	}

	setActive(activeId) {
		let activeLink = null

		this.links.forEach((link) => {
			const isActive = link.getAttribute('href') === `#${activeId}`
			link.classList.toggle('accordion-list-sidebar__link--active', isActive)
			if (isActive) {
				activeLink = link
			}
		})

		// On mobile, scroll the active link to the left edge
		if (activeLink && isMobile()) {
			this.scrollLinkIntoView(activeLink)
		}
	}

	scrollLinkIntoView(link) {
		const list = this.querySelector('.accordion-list-sidebar__list')
		if (!list) return

		// Get the link's parent li element
		const listItem = link.closest('li')
		if (!listItem) return

		// Calculate scroll position to align link to left edge
		const listRect = list.getBoundingClientRect()
		const itemRect = listItem.getBoundingClientRect()
		const scrollLeft = list.scrollLeft + (itemRect.left - listRect.left)

		list.scrollTo({
			left: scrollLeft,
			behavior: 'smooth',
		})
	}

	initMobileSticky() {
		// Only run on mobile
		if (isDesktop()) return

		// Find parent accordion-list container
		this.parentSection = this.closest('.accordion-list')

		// Store original position
		this.stickyTriggerPoint = this.getBoundingClientRect().top + window.scrollY - this.getHeaderOffset()

		// Track hidden state for hysteresis
		this.isCurrentlyHidden = false
		this.scrollRaf = null

		// Bind scroll handler with requestAnimationFrame
		this.onScroll = () => {
			if (this.scrollRaf) return
			this.scrollRaf = requestAnimationFrame(() => {
				this.scrollRaf = null
				this.updateMobileSticky()
			})
		}

		// Use passive listener for better scroll performance
		window.addEventListener('scroll', this.onScroll, { passive: true })

		// Check initial scroll position on load
		this.updateMobileSticky()

		// Recalculate on resize
		this.handleResize = () => {
			if (isDesktop()) {
				this.classList.remove('is-fixed')
				this.classList.remove('is-hidden')
				this.isCurrentlyHidden = false
			} else {
				this.classList.remove('is-fixed')
				this.classList.remove('is-hidden')
				this.isCurrentlyHidden = false
				this.stickyTriggerPoint = this.getBoundingClientRect().top + window.scrollY - this.getHeaderOffset()
				this.updateMobileSticky()
			}
		}

		window.addEventListener('resize', this.handleResize, { passive: true })
	}

	updateMobileSticky() {
		if (isDesktop()) {
			this.classList.remove('is-fixed')
			this.classList.remove('is-hidden')
			this.isCurrentlyHidden = false
			return
		}

		const headerOffset = this.getHeaderOffset()

		// Recalculate trigger point while in normal flow to avoid stale values.
		if (!this.classList.contains('is-fixed')) {
			this.stickyTriggerPoint = this.getBoundingClientRect().top + window.scrollY - headerOffset
		}

		const shouldBeFixed = window.scrollY >= this.stickyTriggerPoint

		// Check if viewport has scrolled past the parent section (with hysteresis buffer)
		let pastSection = false
		if (this.parentSection) {
			const sectionBottom = this.parentSection.getBoundingClientRect().bottom
			// Hide when section bottom is above header
			// Only re-show when section bottom is 60px below header (prevents flicker at boundary)
			if (this.isCurrentlyHidden) {
				pastSection = sectionBottom <= headerOffset + 60
			} else {
				pastSection = sectionBottom <= headerOffset
			}
		}

		this.isCurrentlyHidden = shouldBeFixed && pastSection
		this.classList.toggle('is-fixed', shouldBeFixed && !pastSection)
		this.classList.toggle('is-hidden', this.isCurrentlyHidden)
	}

	disconnectedCallback() {
		if (this.observer) {
			this.observer.disconnect()
		}
		if (this.onScroll) {
			window.removeEventListener('scroll', this.onScroll)
		}
		if (this.handleResize) {
			window.removeEventListener('resize', this.handleResize)
		}
	}
}

customElements.define('accordion-list-sidebar', AccordionListSidebar)
